import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'mehrutherm-theme';
const PREFERENCE_STORAGE_KEY = 'mehrutherm-theme-pref';

function readSystemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readInitialPreference(): ThemePreference {
  const stored = localStorage.getItem(PREFERENCE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  const legacy = localStorage.getItem(STORAGE_KEY);
  if (legacy === 'light' || legacy === 'dark') {
    return legacy;
  }
  return 'system';
}

function effectiveTheme(pref: ThemePreference): Theme {
  if (pref === 'system') return readSystemDark() ? 'dark' : 'light';
  return pref;
}

/**
 * Three-way theme preference (light/dark/system). The applied theme is what
 * gets written to <html>; the preference is what the user chose. Persisted to
 * localStorage; server-side persistence lives at /me/preferences and is wired
 * via the Appearance settings tab.
 */
export function useTheme(): {
  theme: Theme;
  preference: ThemePreference;
  setPreference: (p: ThemePreference) => void;
  toggle: () => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(readInitialPreference);
  const [theme, setTheme] = useState<Theme>(() => effectiveTheme(readInitialPreference()));

  useEffect(() => {
    const next = effectiveTheme(preference);
    setTheme(next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    document.documentElement.dataset.theme = next;
    localStorage.setItem(PREFERENCE_STORAGE_KEY, preference);
    localStorage.setItem(STORAGE_KEY, next);
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = mq.matches ? 'dark' : 'light';
      setTheme(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.dataset.theme = next;
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  return {
    theme,
    preference,
    setPreference: setPreferenceState,
    toggle: () =>
      setPreferenceState((p) =>
        effectiveTheme(p) === 'dark' ? 'light' : 'dark',
      ),
  };
}
